import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SPAM_REASONS } from "../utils";

/**
 * Modal that captures the required Spam classification reason before the
 * backend will accept a status="spam" change.
 *
 * Props:
 *   open       — boolean
 *   onCancel   — () => void
 *   onConfirm  — ({ spam_reason, spam_reason_note }) => Promise<void>
 *   submitting — boolean
 */
export default function SpamReasonDialog({ open, onCancel, onConfirm, submitting }) {
  const [reason, setReason] = useState("bot_ai");
  const [note, setNote] = useState("");
  const isOther = reason === "other";
  const canSubmit = reason && (!isOther || note.trim().length > 0);

  const reset = () => {
    setReason("bot_ai");
    setNote("");
  };

  const handleConfirm = async () => {
    if (!canSubmit) return;
    await onConfirm({ spam_reason: reason, spam_reason_note: isOther ? note.trim() : "" });
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          onCancel();
          reset();
        }
      }}
    >
      <DialogContent className="max-w-md" data-testid="spam-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <AlertTriangle className="h-5 w-5" /> Mark as Spam
          </DialogTitle>
          <DialogDescription>
            Spam leads are excluded from the active pipeline, conversion metrics, and reminders.
            Pick a reason so the audit trail is searchable later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3" data-testid="spam-reason-options">
          {SPAM_REASONS.map((r) => (
            <label
              key={r.value}
              className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                reason === r.value
                  ? "border-rose-300 bg-rose-50"
                  : "border-slate-200 hover:bg-slate-50"
              }`}
              data-testid={`spam-reason-${r.value}`}
            >
              <input
                type="radio"
                name="spam-reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
                className="mt-1"
              />
              <span className="text-sm text-slate-800">{r.label}</span>
            </label>
          ))}

          {isOther && (
            <div className="space-y-1">
              <Label htmlFor="spam-reason-note" className="text-xs">
                Required: describe the reason
              </Label>
              <Textarea
                id="spam-reason-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Sent from agency selling SEO services"
                data-testid="spam-reason-note"
                maxLength={500}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => { onCancel(); reset(); }}
            disabled={submitting}
            data-testid="spam-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!canSubmit || submitting}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="spam-confirm-btn"
          >
            Mark as Spam
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
